import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getEmbedUrl } from '../components/course/LessonPlayer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Film, Upload, Play, Trash2, Clock, CheckCircle, 
  XCircle, X, Youtube, FileVideo, AlertCircle, Eye, Link as LinkIcon, Loader2,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppShell from '../components/AppShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

export default function VideoLibrary() {
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [myVideos, setMyVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'youtube'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    display_on_profile: true
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (!user) {
      navigate('/login');
      return;
    }
    if (!['growth', 'founder'].includes(user.membership_tier) && !user.is_admin) {
      toast.error('Video Library is only available for Growth and Founder members');
      navigate('/dashboard');
      return;
    }
    fetchMyVideos();
  }, [user, authLoading]);

  const fetchMyVideos = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/video-library/my-videos`,
        { headers: getAuthHeader() }
      );
      setMyVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 500MB limit');
      return;
    }

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    setSelectedFile(file);
    if (!formData.title) {
      setFormData(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Please enter a video title');
      return;
    }

    if (uploadType === 'file' && !selectedFile) {
      toast.error('Please select a video file');
      return;
    }

    if (uploadType === 'youtube' && !formData.youtube_url) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      if (uploadType === 'file') {
        // Chunked file upload for large files
        const chunkSize = 5 * 1024 * 1024; // 5MB chunks
        const totalChunks = Math.ceil(selectedFile.size / chunkSize);
        
        // Create upload session
        const sessionRes = await axios.post(
          `${API_URL}/api/video-library/upload/start`,
          {
            filename: selectedFile.name,
            file_size: selectedFile.size,
            title: formData.title,
            description: formData.description,
            display_on_profile: formData.display_on_profile
          },
          { headers: getAuthHeader() }
        );
        
        const uploadId = sessionRes.data.upload_id;

        // Upload chunks
        for (let i = 0; i < totalChunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, selectedFile.size);
          const chunk = selectedFile.slice(start, end);

          const chunkFormData = new FormData();
          chunkFormData.append('chunk', chunk);
          chunkFormData.append('chunk_index', i);
          chunkFormData.append('total_chunks', totalChunks);

          await axios.post(
            `${API_URL}/api/video-library/upload/${uploadId}/chunk`,
            chunkFormData,
            { 
              headers: { 
                ...getAuthHeader(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        // Complete upload
        await axios.post(
          `${API_URL}/api/video-library/upload/${uploadId}/complete`,
          {},
          { headers: getAuthHeader() }
        );

        toast.success('Video uploaded! It will be reviewed by admin before being visible.');
      } else {
        // YouTube URL submission
        await axios.post(
          `${API_URL}/api/video-library/youtube`,
          {
            youtube_url: formData.youtube_url,
            title: formData.title,
            description: formData.description,
            display_on_profile: formData.display_on_profile
          },
          { headers: getAuthHeader() }
        );
        toast.success('YouTube video submitted for approval!');
      }

      // Reset form
      setFormData({ title: '', description: '', youtube_url: '', display_on_profile: true });
      setSelectedFile(null);
      setShowUploadForm(false);
      fetchMyVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (videoId) => {
    try {
      await axios.delete(
        `${API_URL}/api/video-library/${videoId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Video deleted');
      if (selectedVideo?.id === videoId) {
        setSelectedVideo(null);
      }
      setDeleteTarget(null);
      fetchMyVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete video');
      setDeleteTarget(null);
    }
  };

  const handleToggleFeature = async (video) => {
    const willBeFeatured = !video.featured_on_microsite;
    try {
      await axios.put(
        `${API_URL}/api/video-library/${video.id}/feature`,
        { featured: willBeFeatured },
        { headers: getAuthHeader() },
      );
      toast.success(
        willBeFeatured
          ? 'Featured on your micro-site'
          : 'Removed from micro-site',
      );
      fetchMyVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
            <CheckCircle className="w-3 h-3" /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
            <Clock className="w-3 h-3" /> Pending Review
          </span>
        );
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user || (!['growth', 'founder'].includes(user.membership_tier) && !user.is_admin)) {
    return null;
  }

  return (
    <AppShell active="video-library" title="Video Library">
      <div className="min-h-full">
      {/* Sub-header — page title + upload CTA (sidebar/topbar come from AppShell) */}
      <div className="border-b border-white/5 bg-brand-surface/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-playfair text-white" data-testid="video-library-title">Your Video Library</h1>
            <p className="text-sm text-gray-400 mt-1">
              Curate up to <span className="text-brand-gold font-medium">6 videos</span> to feature on your public micro-site.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 rounded-full border border-brand-gold/30 bg-brand-gold/10 text-brand-gold text-sm font-medium flex items-center gap-2" data-testid="featured-count">
              <Star className="w-3.5 h-3.5 fill-current" />
              {myVideos.filter(v => v.featured_on_microsite).length} / 6 featured
            </div>
            <Button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="bg-brand-gold text-black hover:brightness-110 font-semibold"
              data-testid="upload-video-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Upload Video
            </Button>
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <div>
                <h3 className="font-semibold text-foreground">{selectedVideo.title}</h3>
                {getStatusBadge(selectedVideo.status)}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedVideo(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="aspect-video bg-black">
              {selectedVideo.video_type === 'youtube' ? (
                <iframe
                  src={getEmbedUrl(selectedVideo.video_url)}
                  className="w-full h-full"
                  allowFullScreen
                  title={selectedVideo.title}
                />
              ) : (
                <video
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  className="w-full h-full"
                  src={`${API_URL}/api/video-library/${selectedVideo.id}/stream?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                >
                  Your browser does not support video playback.
                </video>
              )}
            </div>
            {selectedVideo.description && (
              <div className="p-4 border-t border-border">
                <p className="text-sm text-muted-foreground">{selectedVideo.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="wm-card p-4 rounded-xl mb-6 flex items-start gap-3 border border-primary/20">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-foreground font-medium">Video Approval Required</p>
            <p className="text-sm text-muted-foreground">
              All uploaded videos must be approved by an admin before appearing on your profile or the platform. 
              File uploads are limited to 500MB. You can upload MP4, MOV, or WebM files, or link YouTube videos.
            </p>
          </div>
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <div className="wm-card p-6 rounded-xl mb-8" data-testid="upload-form">
            <h2 className="text-lg font-semibold text-foreground mb-4">Upload New Video</h2>
            
            {/* Upload Type Tabs */}
            <div className="flex gap-2 mb-6">
              <Button
                variant={uploadType === 'file' ? 'default' : 'outline'}
                onClick={() => setUploadType('file')}
                className={uploadType === 'file' ? 'bg-primary text-primary-foreground' : 'border-border text-muted-foreground'}
              >
                <FileVideo className="w-4 h-4 mr-2" /> Upload File
              </Button>
              <Button
                variant={uploadType === 'youtube' ? 'default' : 'outline'}
                onClick={() => setUploadType('youtube')}
                className={uploadType === 'youtube' ? 'bg-red-600 text-white' : 'border-border text-muted-foreground'}
              >
                <Youtube className="w-4 h-4 mr-2" /> YouTube Link
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {uploadType === 'file' ? (
                <div>
                  <Label className="text-foreground">Video File *</Label>
                  <div 
                    className="mt-1 border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {selectedFile ? (
                      <div>
                        <FileVideo className="w-12 h-12 text-primary mx-auto mb-2" />
                        <p className="text-foreground font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-foreground">Click to select or drag and drop</p>
                        <p className="text-sm text-muted-foreground">MP4, MOV, WebM up to 500MB</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="youtube_url" className="text-foreground">YouTube URL *</Label>
                  <Input
                    id="youtube_url"
                    value={formData.youtube_url}
                    onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="mt-1 bg-input border-border text-foreground"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="title" className="text-foreground">Video Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="My Business Video"
                  className="mt-1 bg-input border-border text-foreground"
                  data-testid="video-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-foreground">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your video..."
                  rows={3}
                  className="mt-1 bg-input border-border text-foreground"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="display_on_profile"
                  checked={formData.display_on_profile}
                  onChange={(e) => setFormData({ ...formData, display_on_profile: e.target.checked })}
                  className="rounded border-border"
                />
                <Label htmlFor="display_on_profile" className="text-sm text-muted-foreground cursor-pointer">
                  Display on my public profile (once approved)
                </Label>
              </div>

              {/* Upload Progress */}
              {uploading && uploadType === 'file' && (
                <div>
                  <div className="flex justify-between text-sm text-muted-foreground mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={uploading}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="submit-upload-btn"
                >
                  {uploading ? 'Uploading...' : 'Submit for Approval'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowUploadForm(false);
                    setSelectedFile(null);
                    setFormData({ title: '', description: '', youtube_url: '', display_on_profile: true });
                  }}
                  className="border-border text-foreground"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* My Videos Grid */}
        <h2 className="text-xl font-semibold text-foreground mb-4">
          {loading ? 'Loading...' : `My Videos (${myVideos.length})`}
        </h2>

        {!loading && myVideos.length === 0 ? (
          <div className="text-center py-16 wm-card rounded-xl">
            <Film className="w-16 h-16 text-primary/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Videos Yet</h3>
            <p className="text-muted-foreground mb-4">
              Upload your first video to share with the WeMasterminds community
            </p>
            <Button onClick={() => setShowUploadForm(true)} className="bg-primary text-primary-foreground">
              <Upload className="w-4 h-4 mr-2" /> Upload Your First Video
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {myVideos.map(video => (
              <div key={video.id} className={`relative rounded-xl overflow-hidden bg-brand-surface transition-all group ${video.featured_on_microsite ? 'border-2 border-brand-gold/40' : 'border border-white/5 hover:border-white/15'}`} data-testid="video-card">
                <div className="aspect-video bg-slate-900 relative">
                  {video.thumbnail_url ? (
                    <img
                      src={video.thumbnail_url.startsWith('http') ? video.thumbnail_url : `${API_URL}${video.thumbnail_url}`}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-surface2 to-brand-surface">
                      {video.video_type === 'youtube' ? (
                        <Youtube className="w-12 h-12 text-red-500/50" />
                      ) : (
                        <FileVideo className="w-12 h-12 text-brand-gold/50" />
                      )}
                    </div>
                  )}

                  {/* Featured star icon top-right */}
                  {video.status === 'approved' && (
                    <button
                      onClick={() => handleToggleFeature(video)}
                      className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        video.featured_on_microsite
                          ? 'bg-brand-gold/90 text-black'
                          : 'bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-brand-gold/90 hover:text-black'
                      }`}
                      title={video.featured_on_microsite ? 'Unfeature from micro-site' : 'Feature on micro-site (max 6)'}
                      data-testid={`video-feature-toggle-${video.id}`}
                    >
                      <Star className="w-4 h-4" fill={video.featured_on_microsite ? 'currentColor' : 'none'} />
                    </button>
                  )}

                  {/* Featured badge top-left */}
                  {video.featured_on_microsite && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-brand-gold text-black text-[10px] font-bold rounded">FEATURED</div>
                  )}

                  {/* Type badge */}
                  {!video.featured_on_microsite && (
                    <div className="absolute top-2 left-2">
                      {video.video_type === 'youtube' ? (
                        <span className="px-2 py-0.5 bg-rose-500/90 text-white text-[10px] font-bold rounded">YT</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-brand-blue/90 text-white text-[10px] font-bold rounded">UP</span>
                      )}
                    </div>
                  )}

                  {/* Play overlay on hover */}
                  {video.status === 'approved' && (
                    <div
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={() => setSelectedVideo(video)}
                    >
                      <div className="w-14 h-14 rounded-full bg-brand-gold flex items-center justify-center">
                        <Play className="w-6 h-6 text-black fill-current ml-0.5" />
                      </div>
                    </div>
                  )}

                  {/* Duration bottom-right */}
                  {video.duration_seconds && (
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 rounded text-[10px] font-medium text-white">
                      {Math.floor(video.duration_seconds / 60)}:{String(video.duration_seconds % 60).padStart(2, '0')}
                    </div>
                  )}
                </div>

                <div className="p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-white truncate flex-1">{video.title}</p>
                    {video.status !== 'approved' && (
                      <div className="flex-shrink-0">{getStatusBadge(video.status)}</div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {video.video_type === 'youtube' ? 'YouTube' : 'Uploaded'} · {new Date(video.created_at).toLocaleDateString()}
                  </p>

                  {video.status === 'rejected' && video.rejection_reason && (
                    <div className="text-xs text-rose-400 bg-rose-500/10 p-2 rounded mt-2">
                      <strong>Reason:</strong> {video.rejection_reason}
                    </div>
                  )}

                  <div className="flex justify-end gap-1 mt-2">
                    {video.status === 'approved' && (
                      <Button size="sm" variant="ghost" onClick={() => setSelectedVideo(video)} className="text-brand-gold hover:text-brand-gold/80 h-7 px-2">
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(video)} className="text-rose-400 hover:text-rose-300 h-7 px-2" data-testid={`delete-video-${video.id}`} aria-label={`Delete ${video.title}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Delete-video confirmation — replaces window.confirm, which some
          browsers auto-dismiss (making delete silently no-op). */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-video-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              The video is removed from your library and your public profile. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-video-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-video-confirm"
            >
              Delete video
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
